import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TimesheetStoreItem } from '@web/types/redux/stores/timesheet.types';
import { parse } from 'date-fns';
import { useTimesheetSummary } from '../../hooks/useTimesheetSummary';
import { SummaryDateItem, SummaryDurationItem, SummaryItem, SummaryPillStatusItem, SummaryPreviewItem } from '../SummaryBar';

const makeToSummaryItem =
  <T extends SummaryItem>(typename: T['__typename']) =>
  (label: T['label'], value: T['value'], stick = false, testId?: string): T =>
    ({
      __typename: typename,
      label,
      value,
      stick,
      testId,
    }) as T;

const toDateSummaryItem = makeToSummaryItem<SummaryDateItem>('date');
const toDurationSummaryItem = makeToSummaryItem<SummaryDurationItem>('duration');
const toPillStatusSummaryItem = makeToSummaryItem<SummaryPillStatusItem>('pill-status');
const toPreviewItem = makeToSummaryItem<SummaryPreviewItem>('preview');

export const useSummaryData = ({
  startDate,
  endDate,
  glPostDate,
  status,
  showBillable,
}: Pick<TimesheetStoreItem, 'startDate' | 'endDate' | 'glPostDate' | 'status' | 'showBillable'>): SummaryItem[] => {
  const { total, billable, nonBillable } = useTimesheetSummary();
  const { _ } = useLingui();
  return [
    ...(startDate
      ? [toDateSummaryItem(_(msg`Begin date`), parse(startDate, 'yyyy-MM-dd', new Date()))]
      : [toPreviewItem(_(msg`Begin date`))]),
    ...(endDate ? [toDateSummaryItem(_(msg`End date`), parse(endDate, 'yyyy-MM-dd', new Date()))] : [toPreviewItem(_(msg`End date`))]),
    toDurationSummaryItem(_(msg`Total hours`), total, undefined, 'total-hours'),
    ...(showBillable
      ? [
          toDurationSummaryItem(_(msg`Billable hours`), billable, undefined, 'total-billable'),
          toDurationSummaryItem(_(msg`Non-billable hours`), nonBillable, undefined, 'total-non-billable'),
        ]
      : []),
    ...(glPostDate ? [toDateSummaryItem(_(msg`GL post date`), parse(endDate, 'yyyy-MM-dd', new Date()))] : []),
    toPillStatusSummaryItem(_(msg`Status`), status),
  ];
};
